// /settings/earnings — Ambassador-payouts passive-income tracker.
//
// Read-only ledger of the referring artist's 10% share of referred venues'
// subscription revenue (accrued by the referral-payout-accrual sweeper), plus
// the Stripe Connect payout-account status + a "Finish setting up payouts"
// action. The Connect button is gated server-side (`referral_payouts_live`
// flag + Stripe configuration) — it stays visible but explains the wait when
// either isn't ready, matching the Referrals tab's calm-not-broken tone for
// dormant Ambassador-payouts surfaces.

import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Text,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { AppShell } from '../../components/app-shell/AppShell';
import { SettingsTabs } from '../../components/app-shell/SettingsTabs';
import { EmptyState, MotionSection, PageHero, SectionTitle } from '../../components/ui';
import { ApiError, fetchJson, mutateJson } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { showToast } from '../../lib/toast';

type ReferralPayoutItem = {
  id: string;
  venue_name: string;
  gross_amount_minor: number;
  currency: string;
  rate_bps: number;
  payout_amount_minor: number;
  state: 'accrued' | 'pending' | 'held' | 'paid' | 'failed' | 'void';
  accrued_at: string;
  paid_at: string | null;
  failed_at: string | null;
};

type ReferralPayoutsResponse = {
  ok: true;
  items: ReferralPayoutItem[];
  totals: { accruedMinor: number; paidMinor: number };
};

type PayoutAccountResponse = {
  status: 'not_started' | 'onboarding' | 'active' | 'restricted';
  payoutsEnabled: boolean;
};

type ConnectResponse = { ok: true; url: string };

export const Route = createFileRoute('/settings/earnings')({
  component: EarningsSettingsPage,
});

function formatMinor(amountMinor: number, currency: string): string {
  return `${(amountMinor / 100).toFixed(2)} ${currency.toUpperCase()}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function stateBadge(state: ReferralPayoutItem['state']) {
  switch (state) {
    case 'paid':
      return (
        <Badge color="brand" variant="filled" size="sm">
          Paid
        </Badge>
      );
    case 'failed':
      return (
        <Badge color="red" variant="light" size="sm">
          Payout failed
        </Badge>
      );
    case 'pending':
      return (
        <Badge color="sun" variant="light" size="sm">
          Sending
        </Badge>
      );
    case 'held':
      return (
        <Badge color="orange" variant="light" size="sm">
          Under review
        </Badge>
      );
    case 'void':
      return (
        <Badge color="gray" variant="light" size="sm">
          Void
        </Badge>
      );
    default:
      return (
        <Badge color="gray" variant="light" size="sm">
          Accrued
        </Badge>
      );
  }
}

function EarningsSettingsPage() {
  const { user, capabilities, isLoading } = useAuth();
  const queryClient = useQueryClient();

  const ledgerQuery = useQuery({
    enabled: capabilities?.isArtist === true,
    queryKey: ['me', 'referral-payouts'],
    queryFn: () => fetchJson<ReferralPayoutsResponse>('/api/me/referral-payouts'),
  });

  const accountQuery = useQuery({
    enabled: capabilities?.isArtist === true,
    queryKey: ['me', 'referral-payout-account'],
    queryFn: () => fetchJson<PayoutAccountResponse>('/api/me/referral-payout-account'),
  });

  const connectMutation = useMutation({
    mutationFn: () => mutateJson<ConnectResponse>('/api/me/referral-payout-account/connect'),
    onSuccess: (res) => {
      window.location.href = res.url;
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 403) {
        showToast({
          kind: 'info',
          message: 'Referral payouts are not live yet. Your earnings still accrue in the meantime.',
        });
        return;
      }
      showToast({ kind: 'error', message: 'Could not start payout setup. Try again shortly.' });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['me', 'referral-payout-account'] });
    },
  });

  if (isLoading) {
    return (
      <AppShell tone="form">
        <Loader />
      </AppShell>
    );
  }

  if (!user) {
    return (
      <AppShell tone="form">
        <Text>Sign in to view your earnings.</Text>
      </AppShell>
    );
  }

  const totals = ledgerQuery.data?.totals ?? { accruedMinor: 0, paidMinor: 0 };
  const items = ledgerQuery.data?.items ?? [];
  const accountStatus = accountQuery.data?.status ?? 'not_started';

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Settings"
            title="Earnings"
            lede="Your 10% referral share, tracked as it accrues: 10% of every subscription payment (excluding GST) made by a host you referred. Paid once a month, on the 7th, for everything earned the month before."
          />
        </MotionSection>

        <SettingsTabs current="earnings" />

        {capabilities?.isArtist !== true ? (
          <Card withBorder padding="lg" radius="md">
            <Stack gap="sm">
              <SectionTitle title="Earnings needs an artist profile" order={3} />
              <Text size="sm" c="dimmed">
                Referral earnings track against your artist account. Add an
                artist profile, then come back here.
              </Text>
            </Stack>
          </Card>
        ) : (
          <>
            <Card withBorder padding="lg" radius="md">
              <Stack gap="md">
                <Group justify="space-between" align="flex-start" wrap="wrap">
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Accrued, not yet paid
                    </Text>
                    <Text size="xl" fw={700}>
                      {formatMinor(totals.accruedMinor, 'AUD')}
                    </Text>
                  </Stack>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Paid out to date
                    </Text>
                    <Text size="xl" fw={700}>
                      {formatMinor(totals.paidMinor, 'AUD')}
                    </Text>
                  </Stack>
                </Group>

                <Box
                  style={{
                    borderTop: '1px solid var(--mantine-color-ink-1)',
                    paddingTop: 'var(--mantine-spacing-md)',
                  }}
                >
                  <Group justify="space-between" align="center" wrap="wrap">
                    <Stack gap={2}>
                      <Text fw={600} size="sm">
                        Payout account
                      </Text>
                      <Text size="xs" c="dimmed">
                        {accountStatus === 'active'
                          ? 'Connected — paid here on the 7th of each month.'
                          : accountStatus === 'onboarding'
                            ? 'Setup started — finish it to receive your first payout.'
                            : accountStatus === 'restricted'
                              ? 'Stripe needs more information before payouts can resume.'
                              : "You haven't set up a payout destination yet."}
                      </Text>
                    </Stack>
                    {accountStatus !== 'active' && (
                      <Button
                        variant="light"
                        loading={connectMutation.isPending}
                        onClick={() => connectMutation.mutate()}
                      >
                        {accountStatus === 'not_started'
                          ? 'Set up payouts'
                          : 'Finish setting up payouts'}
                      </Button>
                    )}
                  </Group>
                </Box>
              </Stack>
            </Card>

            <Card withBorder padding={0} radius="md">
              <Box p="lg" pb="md">
                <SectionTitle title="Earnings history" order={3} />
              </Box>
              {ledgerQuery.isLoading ? (
                <Box p="lg">
                  <Loader size="sm" />
                </Box>
              ) : items.length === 0 ? (
                <Box p="lg" pt={0}>
                  <EmptyState
                    title="No earnings yet"
                    body="Once a venue you referred pays their first subscription invoice, your 10% share appears here."
                  />
                </Box>
              ) : (
                <Stack gap={0}>
                  {items.map((item, idx) => (
                    <Box
                      key={item.id}
                      p="lg"
                      style={{
                        borderTop: idx === 0 ? 'none' : '1px solid var(--mantine-color-ink-1)',
                      }}
                    >
                      <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Stack gap={4}>
                          <Text fw={600}>{item.venue_name}</Text>
                          <Text size="xs" c="dimmed">
                            Accrued {formatDate(item.accrued_at)} · {item.rate_bps / 100}% of{' '}
                            {formatMinor(item.gross_amount_minor, item.currency)}
                          </Text>
                        </Stack>
                        <Stack gap={4} align="flex-end">
                          {stateBadge(item.state)}
                          <Text fw={600} size="sm">
                            {formatMinor(item.payout_amount_minor, item.currency)}
                          </Text>
                        </Stack>
                      </Group>
                    </Box>
                  ))}
                </Stack>
              )}
            </Card>
          </>
        )}
      </Stack>
    </AppShell>
  );
}
